/**
 * Comments command handlers
 */

import { createCommentService, createAttachmentService, createTaskService, getApiUrl } from '../utils/client.js';
import { buildAttachmentFileParts } from '../utils/attach-files.js';
import { chalk, formatOutput, formatSuccess, formatInfo, makeLink, formatTable, isInteractiveOutput } from '../utils/format.js';
import type { AttachFilesResponse } from '../../../../src/lib/services/attachment.service.js';
import { handleApiError, requireOption } from '../utils/errors.js';
import { cliCommentToHtml, htmlToPlainText } from '../../../../src/utils/cli-rich-text.js';
import { buildCommentLink } from '../../../../src/utils/task-link.js';

export const COMMENT_TEXT_BODY_CONFLICT_ERROR =
  'Cannot pass both --text and --body with different values';
export const COMMENT_TEXT_BODY_REQUIRED_ERROR = 'Required option: --text or --body';

export function resolveCommentAddText(options: {
  text?: string;
  body?: string;
}): string {
  const text = options.text;
  const body = options.body;

  if (text != null && body != null && text !== body) {
    throw new Error(COMMENT_TEXT_BODY_CONFLICT_ERROR);
  }

  const resolved = text ?? body;
  if (!resolved) {
    throw new Error(COMMENT_TEXT_BODY_REQUIRED_ERROR);
  }

  return resolved;
}

export async function listComments(
  ticket: string,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    requireOption(ticket, 'ticket');
    const service = await createCommentService(token, apiUrl);
    const result = await service.getComments({ ticket_number: ticket });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      if (isInteractiveOutput()) {
        console.log('');
        console.log(chalk.bold.white(`  Comments on ${chalk.cyan(ticket)}`));
      }

      if (result.comments && result.comments.length > 0) {
        // Fetch task once to get projectId for comment links
        let projectId: number | null = null;
        let appBase: string | null = null;
        try {
          const taskService = await createTaskService(token, apiUrl);
          const taskResult = await taskService.getTask({ ticket_number: [ticket] });
          const task = taskResult?.tasks?.[0];
          if (task?.projectId) {
            projectId = task.projectId;
            appBase = new URL(getApiUrl(apiUrl)).origin;
          }
        } catch {
          // Silently skip — links are a bonus
        }

        const rows = result.comments.map((c: any, idx: number) => {
          const author = c.createdBy?.displayName ?? c.creator?.displayName ?? 'Unknown';
          const date = c.createdAt ? new Date(c.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) : '';
          const body = c.text ?? c.commentText ?? c.comment_text ?? '';
          const plain = htmlToPlainText(String(body));

          const commentUrl = appBase && projectId
            ? buildCommentLink(appBase, projectId, ticket, c.id)
            : null;

          return [c.id ?? idx + 1, author, date, plain || '(no text)', commentUrl ?? ''];
        });
        const termWidth = process.stdout.columns || 120;
        const commentWidth = Math.max(40, Math.floor(termWidth * 0.35));
        const linkWidth = Math.max(30, Math.floor(termWidth * 0.25));
        console.log(formatTable(['ID', 'Author', 'Created', 'Comment', 'Link'], rows, [undefined, 20, 24, commentWidth, linkWidth]));
        if (isInteractiveOutput()) console.log('');
      } else {
        console.log(chalk.dim('\n  No comments\n'));
      }
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function addComment(
  ticket: string,
  text: string,
  token?: string,
  apiUrl?: string,
  options?: { attach?: string[]; json?: boolean }
): Promise<void> {
  try {
    requireOption(ticket, 'ticket');
    requireOption(text, 'text');

    const json = options?.json;

    const service = await createCommentService(token, apiUrl);
    const result = await service.addComment({
      ticket_number: ticket,
      text: cliCommentToHtml(text),
    });

    let attachResult: AttachFilesResponse | null = null;
    if (options?.attach?.length) {
      const attachmentService = await createAttachmentService(token, apiUrl);
      const files = await buildAttachmentFileParts(options.attach);
      attachResult = await attachmentService.attachFiles({
        ticket_number: ticket,
        comment_id: result.comment.id,
        files,
      });
    }

    if (json) {
      const out =
        attachResult && options?.attach?.length
          ? { ...result, attachments_uploaded: attachResult }
          : result;
      console.log(formatOutput(out, true));
    } else {
      console.log(formatSuccess(`Added comment to ${ticket}`));
      if (attachResult?.attachments?.length) {
        for (const a of attachResult.attachments) {
          const line = a.url ? `${a.fileName} — ${a.url}` : a.fileName;
          console.log(formatInfo(`Attachment: ${line}`));
        }
      }
      try {
        const taskService = await createTaskService(token, apiUrl);
        const taskResult = await taskService.getTask({ ticket_number: [ticket] });
        const task = taskResult?.tasks?.[0];
        if (task?.projectId) {
          const appBase = new URL(getApiUrl(apiUrl)).origin;
          const commentUrl = buildCommentLink(appBase, task.projectId, ticket, result.comment.id);
          if (commentUrl) {
            console.log(formatInfo(makeLink(commentUrl, commentUrl)));
          }
        }
      } catch {
        // Silently skip — link is a bonus, not critical
      }
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function updateComment(
  commentId: number,
  text: string,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    requireOption(commentId.toString(), 'comment-id');
    requireOption(text, 'text');

    const service = await createCommentService(token, apiUrl);
    const result = await service.updateComment({
      comment_id: commentId,
      text: cliCommentToHtml(text),
    });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log(formatSuccess(`Updated comment ${commentId}`));
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function deleteComment(
  commentId: number,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    requireOption(commentId.toString(), 'comment-id');

    const service = await createCommentService(token, apiUrl);
    const result = await service.deleteComment({
      comment_id: commentId,
    });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log(formatSuccess(`Deleted comment ${commentId}`));
    }
  } catch (error) {
    handleApiError(error);
  }
}
